/**
 * SPAACIO Exit Intent Popup
 * Sistema de re-engagement para visitantes que están por salir.
 *
 * v1.1.0 - Hidratación en tiempo real
 *   - Productos del popup se hidratan con datos frescos antes de mostrarse
 *   - Resuelve precios obsoletos durante Hot Sale, etc.
 *
 * Features:
 *   - Exit-intent desktop (mouseleave por arriba)
 *   - Heurística móvil (scroll rápido arriba + tiempo mínimo)
 *   - Title flicker en pestaña inactiva
 *   - Cooldown de 7 días por defecto
 *   - Mínimo 1 producto visto antes de disparar
 *   - No aparece en checkout / cart
 */

(function () {
  'use strict';

  // ============================================
  // STORAGE KEYS Y CONFIG POR DEFECTO
  // ============================================
  const STORAGE_KEY_SHOWN = 'spaacio_exit_popup_shown_at';
  const STORAGE_KEY_DISMISSED = 'spaacio_exit_popup_dismissed';
  const SESSION_KEY_SHOWN = 'spaacio_exit_popup_session_shown';

  // ============================================
  // ESTADO INTERNO
  // ============================================
  let popupEl = null;
  let config = null;
  let originalTitle = '';
  let flickerInterval = null;
  let pageLoadedAt = Date.now();
  let lastScrollY = 0;
  let lastScrollTime = Date.now();
  let isInitialized = false;
  let isFetchingProducts = false; // Evita disparos múltiples mientras hidrata

  // ============================================
  // UTILIDADES DE STORAGE
  // ============================================
  function getTimestamp(key) {
    try {
      const v = localStorage.getItem(key);
      return v ? parseInt(v, 10) : 0;
    } catch (e) {
      return 0;
    }
  }

  function setTimestamp(key, value) {
    try {
      localStorage.setItem(key, String(value));
    } catch (e) {
      console.warn('[SPAACIO ExitIntent] Storage error:', e);
    }
  }

  function isInCooldown() {
    const cooldownDays = config.cooldownDays || 7;
    const cooldownMs = cooldownDays * 24 * 60 * 60 * 1000;
    const shownAt = getTimestamp(STORAGE_KEY_SHOWN);
    return shownAt > 0 && (Date.now() - shownAt) < cooldownMs;
  }

  function alreadyShownThisSession() {
    try {
      return sessionStorage.getItem(SESSION_KEY_SHOWN) === '1';
    } catch (e) {
      return false;
    }
  }

  function markShownThisSession() {
    try {
      sessionStorage.setItem(SESSION_KEY_SHOWN, '1');
    } catch (e) {
      /* silent */
    }
  }

  // ============================================
  // VALIDACIONES PREVIAS
  // ============================================
  function canShowPopup() {
    if (alreadyShownThisSession()) return false;
    if (isInCooldown()) return false;
    if (isFetchingProducts) return false;

    const minProducts = config.minProductsViewed || 0;
    if (minProducts > 0) {
      if (!window.SpaacioRecentlyViewed) return false;
      const viewed = window.SpaacioRecentlyViewed.getAll();
      if (!viewed || viewed.length < minProducts) return false;
    }

    const minSecondsOnPage = config.minSecondsOnPage || 5;
    if ((Date.now() - pageLoadedAt) / 1000 < minSecondsOnPage) return false;

    return true;
  }

  // ============================================
  // RENDERIZADO DE PRODUCTOS
  // ============================================
  function formatPrice(cents) {
    const amount = (cents || 0) / 100;
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  }

  function calculateDiscount(price, comparePrice) {
    if (!comparePrice || comparePrice <= price) return 0;
    return Math.round((1 - price / comparePrice) * 100);
  }

  function renderProductsIntoGrid(products) {
    const grid = popupEl.querySelector('[data-sp-exit-grid]');
    const cardTemplate = popupEl.querySelector('[data-sp-exit-card-template]');
    if (!grid || !cardTemplate) return;

    grid.innerHTML = '';

    products.forEach(product => {
      const card = cardTemplate.content.cloneNode(true);
      const link = card.querySelector('.sp-exit-intent__card');
      const img = card.querySelector('.sp-exit-intent__image');
      const title = card.querySelector('.sp-exit-intent__product-title');
      const priceEl = card.querySelector('[data-sp-exit-price]');
      const compareEl = card.querySelector('[data-sp-exit-compare]');
      const badgeEl = card.querySelector('[data-sp-exit-badge]');

      link.href = product.url || '#';
      link.setAttribute('aria-label', product.title || 'Producto');

      if (product.image) {
        img.src = product.image;
        img.alt = product.title || '';
      }

      title.textContent = product.title || '';

      const discount = calculateDiscount(product.price, product.compare_at_price);
      if (discount > 0) {
        priceEl.textContent = formatPrice(product.price);
        priceEl.classList.add('sp-exit-intent__price--sale');
        compareEl.textContent = formatPrice(product.compare_at_price);
        compareEl.hidden = false;
        badgeEl.textContent = '-' + discount + '%';
        badgeEl.hidden = false;
      } else {
        priceEl.textContent = formatPrice(product.price);
      }

      grid.appendChild(card);
    });
  }

  // ============================================
  // CTA WHATSAPP CON PRODUCTOS PRE-RELLENADOS
  // ============================================
  function buildWhatsAppLink(products) {
    const phone = (config.whatsappPhone || '').replace(/\D/g, '');
    if (!phone) return '';

    let message = config.whatsappMessage || '¡Hola! Vi estos productos en spaacio.mx y me interesan:';
    message += '\n\n';

    products.slice(0, 3).forEach((p, i) => {
      const url = window.location.origin + (p.url || '');
      message += `${i + 1}. ${p.title || ''}\n${url}\n\n`;
    });

    message += '¿Me podrían dar más información?';

    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  }

  // ============================================
  // MOSTRAR / OCULTAR POPUP
  // ============================================
  async function showPopup() {
    if (!canShowPopup()) return;
    if (!popupEl) return;

    isFetchingProducts = true;

    // Detener listeners de exit-intent inmediatamente para evitar disparos múltiples
    detachExitListeners();

    try {
      // Obtener productos hidratados (precios frescos)
      let products = [];
      if (window.SpaacioRecentlyViewed && window.SpaacioRecentlyViewed.getDisplayHydrated) {
        const maxProducts = config.maxProducts || 3;
        products = await window.SpaacioRecentlyViewed.getDisplayHydrated('', maxProducts);
      } else if (window.SpaacioRecentlyViewed) {
        // Legacy fallback
        const maxProducts = config.maxProducts || 3;
        products = window.SpaacioRecentlyViewed.getDisplay('', maxProducts);
      }

      if (!products.length) {
        isFetchingProducts = false;
        return; // No mostrar si no hay productos
      }

      renderProductsIntoGrid(products);

      // Configurar WhatsApp link si la CTA está activa
      const waBtn = popupEl.querySelector('[data-sp-exit-whatsapp]');
      if (waBtn && config.showWhatsapp) {
        const waLink = buildWhatsAppLink(products);
        if (waLink) {
          waBtn.href = waLink;
        } else {
          waBtn.hidden = true;
        }
      }

      popupEl.hidden = false;
      requestAnimationFrame(() => {
        popupEl.classList.add('sp-exit-intent--visible');
      });

      document.body.style.overflow = 'hidden';

      setTimestamp(STORAGE_KEY_SHOWN, Date.now());
      markShownThisSession();

      const firstFocusable = popupEl.querySelector('button, a[href]');
      if (firstFocusable) firstFocusable.focus();

      document.dispatchEvent(new CustomEvent('spaacio:exit-popup-shown', {
        detail: { products }
      }));
    } catch (e) {
      console.warn('[SPAACIO ExitIntent] Error mostrando popup:', e);
    } finally {
      isFetchingProducts = false;
    }
  }

  function hidePopup(dismissed = false) {
    if (!popupEl) return;

    popupEl.classList.remove('sp-exit-intent--visible');

    setTimeout(() => {
      popupEl.hidden = true;
      document.body.style.overflow = '';
    }, 250);

    if (dismissed) {
      setTimestamp(STORAGE_KEY_DISMISSED, Date.now());
    }

    document.dispatchEvent(new CustomEvent('spaacio:exit-popup-hidden', {
      detail: { dismissed }
    }));
  }

  // ============================================
  // DETECCIÓN: EXIT-INTENT DESKTOP
  // ============================================
  function onMouseLeave(e) {
    if (e.clientY <= 0) {
      showPopup();
    }
  }

  // ============================================
  // DETECCIÓN: SCROLL RÁPIDO MÓVIL
  // ============================================
  function onScroll() {
    const now = Date.now();
    const currentY = window.scrollY;
    const deltaY = lastScrollY - currentY;
    const deltaTime = now - lastScrollTime;

    if (deltaY > 100 && deltaTime < 300 && deltaTime > 0) {
      if ((now - pageLoadedAt) / 1000 >= 15) {
        showPopup();
      }
    }

    lastScrollY = currentY;
    lastScrollTime = now;
  }

  // ============================================
  // TITLE FLICKER (pestaña inactiva)
  // ============================================
  function startTitleFlicker() {
    if (!config.enableTitleFlicker) return;
    if (flickerInterval) return;

    const messages = (config.flickerMessages || '').split('|').map(s => s.trim()).filter(Boolean);
    if (!messages.length) return;

    let index = 0;
    flickerInterval = setInterval(() => {
      document.title = messages[index % messages.length] + ' | ' + originalTitle;
      index++;
    }, 1500);
  }

  function stopTitleFlicker() {
    if (flickerInterval) {
      clearInterval(flickerInterval);
      flickerInterval = null;
    }
    if (originalTitle) {
      document.title = originalTitle;
    }
  }

  function onVisibilityChange() {
    if (document.hidden) {
      if (window.SpaacioRecentlyViewed) {
        const viewed = window.SpaacioRecentlyViewed.getAll();
        if (viewed && viewed.length > 0) {
          startTitleFlicker();
        }
      }
    } else {
      stopTitleFlicker();
    }
  }

  // ============================================
  // ATTACH / DETACH LISTENERS
  // ============================================
  function attachExitListeners() {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    if (isMobile) {
      lastScrollY = window.scrollY;
      lastScrollTime = Date.now();
      window.addEventListener('scroll', onScroll, { passive: true });
    } else {
      document.addEventListener('mouseleave', onMouseLeave);
    }
  }

  function detachExitListeners() {
    document.removeEventListener('mouseleave', onMouseLeave);
    window.removeEventListener('scroll', onScroll);
  }

  // ============================================
  // SETUP DE EVENTOS DEL MODAL
  // ============================================
  function setupModalEvents() {
    if (!popupEl) return;

    const closeBtn = popupEl.querySelector('[data-sp-exit-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => hidePopup(true));
    }

    const backdrop = popupEl.querySelector('[data-sp-exit-backdrop]');
    if (backdrop) {
      backdrop.addEventListener('click', () => hidePopup(true));
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !popupEl.hidden) {
        hidePopup(true);
      }
    });

    const continueBtn = popupEl.querySelector('[data-sp-exit-continue]');
    if (continueBtn) {
      continueBtn.addEventListener('click', () => hidePopup(false));
    }
  }

  // ============================================
  // INIT
  // ============================================
  function init() {
    if (isInitialized) return;

    popupEl = document.querySelector('[data-sp-exit-intent]');
    if (!popupEl) return;

    try {
      config = JSON.parse(popupEl.dataset.config || '{}');
    } catch (e) {
      console.warn('[SPAACIO ExitIntent] Config parse error:', e);
      return;
    }

    isInitialized = true;
    originalTitle = document.title;
    pageLoadedAt = Date.now();

    setupModalEvents();

    const path = window.location.pathname || '';
    const isCheckoutOrCart = /\/(checkout|cart)/.test(path);

    if (!isCheckoutOrCart) {
      const minSeconds = config.minSecondsOnPage || 5;
      setTimeout(attachExitListeners, minSeconds * 1000);

      if (config.enableTitleFlicker) {
        document.addEventListener('visibilitychange', onVisibilityChange);
      }
    }

    // API pública para debug
    window.SpaacioExitIntent = {
      show: showPopup,
      hide: hidePopup,
      reset: function () {
        try {
          localStorage.removeItem(STORAGE_KEY_SHOWN);
          localStorage.removeItem(STORAGE_KEY_DISMISSED);
          sessionStorage.removeItem(SESSION_KEY_SHOWN);
        } catch (e) { /* silent */ }
      },
      canShow: canShowPopup
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();